import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFullscreen, useToggle } from 'react-use';
import styles from '../../src/assets/sass/options.module.sass';
import { Icons } from '../assets/icons/icons';
import CameraSection from '../components/CameraSection';
import Viewer3d from '../components/Viewer3d/Viewer3d';
import {
  MATERIAL_PROPERTIES_ROTATION_STEP,
  MATERIAL_PROPERTIES_STEP,
  MAX_PROPERTIES_VALUE,
  MIN_PROPERTIES_VALUE,
} from '../components/helper/constants';
import useKeyPressToggle from '../utils/useKeyPressToggle';
import Loader from '../components/Loader';
import CurrentContext from '../utils/CurrentContext';
import useIsMobile from '../utils/useIsMobile';
import { DB } from '../utils/dbApis';
import { getMaterialsData, getProductsData } from '../utils/dbApis';
import {
  FileDownload,
  isAndroid,
  isConfiguratorMode,
  isDebugMode,
  parseFloatValue,
  SuccessToastMessage,
} from '../utils/jsHelper';

const { MeasureIcon, ScreenShotIcon, EmbedIcon, RotateIcon, ARIcon } = Icons;

var isOpen = false;

export default function SmartObjectViewer() {
  const [openSections, setOpenSections] = useState({});
  const { uuid } = useParams();
  const {
    upholsteryFinish,
    setUpholsteryFinish,
    paintColorFinish,
    setPaintColorFinish,
    legFinish,
    setLegFinish,
    legFinishPaintColor,
    setLegFinishPaintColor,
    cushionFinish,
    setCushionFinish,
    woodTopFinish,
    setWoodTopFinish,
    chairFrameFinish,
    setChairFrameFinish,
    retreatFabricFinish,
    setRetreatFabricFinish,
    retreatLegFinish,
    setRetreatLegFinish,
    turnedLegFinish,
    setTurnedLegFinish,
    metalLegFinish,
    setMetalLegFinish,
    headboardFabricFinish,
    setHeadboardFabricFinish,
    currentFinishes,
    setCurrentFinishes,
    defaultFinish,
    setDefaultFinish,
    displayMeasure,
    setIsMeasure,
    productsData,
    autoRotate,
    setAutoRotate,
    productVariants,
    variantName,
    setVariantName,
    currentMaterialFinish,
    setCurrentMaterialFinish,
    // for Ar viewer
    setIsAR,
    setIsARReady,
    ARModelSrc,
    setARModelSrc,
    ARUnicode,
    setARUnicode,
    setLoading,
    setAOIntensity,
  } = useContext(CurrentContext);

  const finishOptions = [
    { key: 'upholsteryFinish', label: 'Upholstery', data: upholsteryFinish },
    { key: 'paintColorFinish', label: 'Paint Color', data: paintColorFinish },
    { key: 'legFinish', label: 'Leg Finish', data: legFinish },
    {
      key: 'legPaintColorFinish',
      label: 'Leg Finish + Paint Color',
      data: legFinishPaintColor,
    },
    { key: 'cushionFinish', label: 'Cushion', data: cushionFinish },
    { key: 'woodTopFinish', label: 'Wood Top', data: woodTopFinish },
    { key: 'chairFrameFinish', label: 'Chair Frame', data: chairFrameFinish },
    {
      key: 'retreatFabricFinish',
      label: 'Retreat Fabrics',
      data: retreatFabricFinish,
    },
    {
      key: 'retreatLegFinish',
      label: 'Retreat Leg Finish',
      data: retreatLegFinish,
    },
    { key: 'metalLegFinish', label: 'Metal Leg Finish', data: metalLegFinish },
    {
      key: 'turnedLegFinish',
      label: 'Turned Leg Finish',
      data: turnedLegFinish,
    },
    {
      key: 'headboardFabricFinish',
      label: 'Headboard Fabric',
      data: headboardFabricFinish,
    },
  ];

  const ref = useRef();

  const [show, toggleFullScreen] = useToggle(false);
  const [isEmbed, setIsEmbed] = useState(false);
  const [isShare, setIsShare] = useState(false);
  const [currentProduct, setCurrentProduct] = useState({});
  const [materialFinishes, setMaterialFinishes] = useState([]);

  const isMobile = useIsMobile();
  const [apiLoading, setApiLoading] = useState(false);
  const [url, setUrl] = useState(null);
  const [showButton, setShowButton] = useState(false);

  const isFullscreen = useFullscreen(ref, show, {
    onClose: () => toggleFullScreen(false),
  });

  // Effect to fetch materials data when the component mounts
  useEffect(() => {
    const fetchMaterialsData = async () => {
      const data = await getMaterialsData();
      const materialsData = data.sort((a, b) => a.id - b.id);
      // console.log("materialsData", materialsData);
      // Map to associate finish types with their respective groups
      const finishesMap = {
        upholsteryFinish: 'Upholstery',
        paintColorFinish: 'Paint_Color',
        legFinish: 'Leg_Finish',
        legFinishPaintColor: 'Leg_Finish_Paint_Color',
        cushionFinish: 'Cushion',
        woodTopFinish: 'Wood_Top',
        chairFrameFinish: 'Chair_Frame',
        retreatFabricFinish: 'Retreat_Fabrics',
        retreatLegFinish: 'Retreat_Leg_Finish',
        turnedLegFinish: 'Turned_Leg_Finish',
        metalLegFinish: 'Metal_Legs',
        headboardFabricFinish: 'Headboard_Fabric',
      };

      // Filter materials data based on groups and update state
      setUpholsteryFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.upholsteryFinish),
        ),
      );
      setPaintColorFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.paintColorFinish),
        ),
      );
      setLegFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.legFinish),
        ),
      );
      setLegFinishPaintColor(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.legFinishPaintColor),
        ),
      );
      setCushionFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.cushionFinish),
        ),
      );
      setWoodTopFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.woodTopFinish),
        ),
      );
      setChairFrameFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.chairFrameFinish),
        ),
      );
      setRetreatFabricFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.retreatFabricFinish),
        ),
      );
      setRetreatLegFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.retreatLegFinish),
        ),
      );
      setTurnedLegFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.turnedLegFinish),
        ),
      );
      setMetalLegFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.metalLegFinish),
        ),
      );
      setHeadboardFabricFinish(
        materialsData.filter((item) =>
          item.groups.includes(finishesMap.headboardFabricFinish),
        ),
      );
    };
    fetchMaterialsData();
  }, []);

  // Custom hook to handle key press toggling for configuration and debug modes
  const { isConfig, isDebug, isMaterialEdit } = useKeyPressToggle();

  useEffect(() => {
    const fetchData = async () => {
      const updatedMaterialFinish = {};

      // Await fetching materialsData
      // const materialsData = await getMaterialsData();
      const data = await getMaterialsData();
      const materialsData = data.sort((a, b) => a.id - b.id);

      // Loop over the currentFinishes object and update currentMaterialFinish
      Object.keys(currentFinishes).forEach((key) => {
        const finishName = currentFinishes[key];

        // Find the corresponding material in materialsData
        const selectedMaterial = materialsData.find(
          (item) => item.name === finishName,
        );

        if (selectedMaterial) {
          updatedMaterialFinish[key] = selectedMaterial;
        }
      });

      // Set currentMaterialFinish with the updated materials
      setCurrentMaterialFinish(updatedMaterialFinish);
    };

    // Call the async function
    fetchData();
  }, [currentFinishes]);

  // Function to handle changes in the selected finish
  const handleFinishChange = (key) => (e) => {
    const selectedValue = e.target.value;

    setCurrentFinishes((prevState) => ({
      ...prevState,
      [key]: selectedValue,
    }));
  };

  // console.log(paintColorFinish,upholsteryFinish,"datatata");

  async function handleDefaultFinish(key, val) {
    // Get the current product data
    const defaultData = await getProductsData();
    const filteredData = defaultData?.find(
      (product) => product.id === currentProduct.id,
    ).default;

    const newUpdatedData = {
      ...filteredData, // copy of the filteredData
      finish: {
        ...filteredData.finish, // Copy the existing finish structure
        [key]: {
          // Only modify the specific key (e.g., 'upholsteryFinish')
          ...filteredData.finish[key], // Copy the existing data for the key
          default: val, // Update only the 'default' value with the new value (val)
        },
      },
    };

    try {
      const { data, error } = await DB.from('Scene_Data')
        .update({
          id: currentProduct?.id,
          title: currentProduct?.title,
          uuid: currentProduct?.uuid,
          default: newUpdatedData,
        })
        .eq('id', currentProduct?.id)
        .select();

      if (error === null) {
        SuccessToastMessage('Default Finish Updated Successfully!!!');
      } else {
        console.log('Error occurred:', error);
      }
    } catch (err) {
      console.error('Unexpected error occurred:', err);
    }
  }

  const handleInputMaterialChange = (type, index, finishKey) => (event) => {
    const value = Number(event.target.value);

    // Update only the specific property (e.g., repeat[0], offset[1])
    setCurrentMaterialFinish((prevState) => ({
      ...prevState,
      [finishKey]: {
        ...prevState[finishKey], // Keep other properties intact
        [type]: [
          ...prevState[finishKey][type].slice(0, index), // Keep unchanged values before `index`
          value, // Update the value at the `index`
          ...prevState[finishKey][type].slice(index + 1), // Keep unchanged values after `index`
        ],
      },
    }));
  };

  const handleInputMaterialChange2 = (property, finishKey) => (event) => {
    const value = Number(event.target.value);

    // Update only the specific property (e.g., repeat[0], offset[1])
    setCurrentMaterialFinish((prevState) => ({
      ...prevState,
      [finishKey]: {
        ...prevState[finishKey], // Keep other properties intact
        [property]: value, // Update the specific property (e.g., aoMapIntensity, normal, etc.)
      },
    }));
  };

  const handleSliderMaterialChange = (property, finishKey) => (value) => {
    // const value = Number(event.target.value);
    // Update only the specific slider value
    setCurrentMaterialFinish((prevState) => ({
      ...prevState,
      [finishKey]: {
        ...prevState[finishKey], // Keep other properties intact
        [property]: value, // Update the specific property (e.g., aoMapIntensity, normal, etc.)
      },
    }));
  };
  async function handleMaterialSave(key) {
    // console.log(key,"clicked");
    // console.log(options,"options")
    // console.log(key.id,key?.repeat[0],key.aoMapIntensity,"hello")
    try {
      const { data, error } = await DB.from('Materials_Data_V2_New')
        .update({
          id: key?.id,
          repeat: [key?.repeat[0], key?.repeat[1]],
          offset: [key?.offset[0], key?.offset[1]],
          aoMapIntensity: key?.aoMapIntensity,
          normal: key?.normal,
          roughness: key?.roughness,
          metalness: key?.metalness,
          clearcoat: key?.clearcoat,
          clearcoatRoughness: key?.clearcoatRoughness,
          rotation: key?.rotation,
        })
        .eq('id', key?.id)
        .select();

      if (error === null) {
        SuccessToastMessage('Material Updated Successfully!!!');
      } else {
        console.log('Error occurred:', error);
      }
    } catch (err) {
      console.error('Unexpected error occurred:', err);
    }
  }

  // Function to render a select dropdown for finishes
  const renderSelect = (label, options, finishKey) => {
    return (
      <div key={finishKey} className={styles.accordionItem}>
        <fieldset>
          <legend>
            {label}
            <span
              className={styles.saveButton}
              onClick={() =>
                handleDefaultFinish(finishKey, currentFinishes[finishKey])
              }
            >
              Set as default
            </span>
          </legend>
          <select
            value={currentFinishes[finishKey]}
            onChange={handleFinishChange(finishKey)}
          >
            <option value="" disabled>
              Select {label}
            </option>
            {/* Render the filtered options */}
            {options.map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </select>
        </fieldset>
        {isMaterialEdit && currentMaterialFinish[finishKey] && (
          <div className={styles.materialFinishBody}>
            {/* <div>
              <span>{currentMaterialFinish[finishKey]?.name}</span>
              <span className={styles.saveButton}>Save</span>
            </div> */}
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Repeat X:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.repeat[0]}
                onChange={handleInputMaterialChange('repeat', 0, finishKey)}
                step={MATERIAL_PROPERTIES_STEP}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Repeat Y:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.repeat[1]}
                onChange={handleInputMaterialChange('repeat', 1, finishKey)}
                step={MATERIAL_PROPERTIES_STEP}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Offset X:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.offset[0]}
                onChange={handleInputMaterialChange('offset', 0, finishKey)}
                step={MATERIAL_PROPERTIES_STEP}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Offset Y:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.offset[1]}
                onChange={handleInputMaterialChange('offset', 1, finishKey)}
                step={MATERIAL_PROPERTIES_STEP}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Ao Map Intensity:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.aoMapIntensity}
                onChange={handleInputMaterialChange2(
                  'aoMapIntensity',
                  finishKey,
                )}
                step={MATERIAL_PROPERTIES_STEP}
                min={MIN_PROPERTIES_VALUE}
                max={MAX_PROPERTIES_VALUE}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Normal:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.normal}
                onChange={handleInputMaterialChange2('normal', finishKey)}
                step={MATERIAL_PROPERTIES_STEP}
                min={MIN_PROPERTIES_VALUE}
                max={MAX_PROPERTIES_VALUE}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Roughness:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.roughness}
                onChange={handleInputMaterialChange2('roughness', finishKey)}
                step={MATERIAL_PROPERTIES_STEP}
                min={MIN_PROPERTIES_VALUE}
                max={MAX_PROPERTIES_VALUE}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                metalness:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.metalness}
                onChange={handleInputMaterialChange2('metalness', finishKey)}
                step={MATERIAL_PROPERTIES_STEP}
                min={MIN_PROPERTIES_VALUE}
                max={MAX_PROPERTIES_VALUE}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Clearcoat:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.clearcoat}
                onChange={handleInputMaterialChange2('clearcoat', finishKey)}
                step={MATERIAL_PROPERTIES_STEP}
                min={MIN_PROPERTIES_VALUE}
                max={MAX_PROPERTIES_VALUE}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                ClearcoatRoughness:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.clearcoatRoughness}
                onChange={handleInputMaterialChange2(
                  'clearcoatRoughness',
                  finishKey,
                )}
                step={MATERIAL_PROPERTIES_STEP}
                min={MIN_PROPERTIES_VALUE}
                max={MAX_PROPERTIES_VALUE}
              />
            </div>
            <div className={styles.materialFinish}>
              <label
                className={styles.materialFinishLabel}
                htmlFor="numberInput"
              >
                Rotation:&nbsp;{' '}
              </label>
              <input
                type="number"
                id="numberInput"
                value={currentMaterialFinish[finishKey]?.rotation}
                onChange={handleInputMaterialChange2('rotation', finishKey)}
                step={MATERIAL_PROPERTIES_ROTATION_STEP}
                // max={MAX_PROPERTIES_VALUE}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 10,
              }}
            >
              <span
                className={styles.saveButton}
                onClick={() =>
                  handleMaterialSave(currentMaterialFinish[finishKey])
                }
              >
                Save
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };
  // console.log(currentMaterialFinish,"currentMaterialFinish");

  const toggleAccordion = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const convertMaterialFinishesToDefaultFinish = (finishes) => {
    const result = {};
    finishes.forEach((item) => {
      result[item.finishType] = {
        default: currentFinishes[item.default],
        reapeatFactor: item.reapeatFactor,
        offsetFactor: item.offsetFactor,
      };
    });
    return result;
  };

  useEffect(() => {
    if (defaultFinish && Object.keys(defaultFinish).length > 0) {
      const finishesArray = Object.entries(defaultFinish).map(
        ([key, value]) => ({
          finishType: key,
          ...value,
        }),
      );
      setMaterialFinishes(finishesArray);
    }
  }, [defaultFinish]);
  // console.log(materialFinishes,"materialFinishes")
  const handleInputChange = (key, type, index) => (event) => {
    const value = Number(event.target.value);

    const updatedMaterialFinishes = [...materialFinishes];

    if (type === 'repeatFactor') {
      updatedMaterialFinishes[key].reapeatFactor[index] = value;
    } else if (type === 'offsetFactor') {
      updatedMaterialFinishes[key].offsetFactor[index] = value;
    }

    // Update the state with the new array
    setMaterialFinishes(updatedMaterialFinishes);

    // Update defaultFinish based on the updated materialFinishes
    const newDefaultFinish = convertMaterialFinishesToDefaultFinish(
      updatedMaterialFinishes,
    );
    setDefaultFinish(newDefaultFinish);
  };

  function handleMeasureTool() {
    setIsMeasure(!displayMeasure);
  }

  // Function for taking screenshot
  const handleTakeScreenshot = () => {
    const canvas = document.querySelector('canvas');
    FileDownload(
      canvas.toDataURL('image/png'),
      `${currentProduct.title}${
        currentProduct.variant ? currentProduct.variant : ''
      }.png`,
    );
  };

  useEffect(() => {
    const data =
      productVariants.length > 1
        ? productVariants.find((product) => product.variant === variantName)
        : productsData.find((product) => product.id === parseInt(uuid));
    // const data = productsData?.find((product) => product.id === parseInt(uuid));
    data && setCurrentProduct(data);
    data && setAOIntensity(data.aoIntensity);
    setIsMeasure(false);
  }, [variantName]);
  // console.log(currentProduct,"currentProduct");
  // console.log(defaultFinish,"defaultFinish");
  // console.log(materialFinishes,"materialFinishes");

  async function handleUpdateFinish(key) {
    // console.log(key, "updateFinish");

    const defaultData = await getProductsData();
    const filteredData = defaultData?.find(
      (product) => product.id === currentProduct.id,
    ).default;
    const updatedData = currentProduct.default;

    // console.log(filteredData, updatedData, "datadatadata");
    // console.log(filteredData.finish[key], "datadatadata");
    // console.log(updatedData.finish[key], "datadatadata");

    const newUpdatedData = {
      ...filteredData, //copy of the filteredData
      finish: {
        ...filteredData.finish, // Copy the existing finish structure
        [key]: {
          // Only modify the specific key (e.g., 'upholsteryFinish')
          ...filteredData.finish[key], // Copy the existing data for the key
          ...updatedData.finish[key], // Update with the data from updatedData for the specific key
        },
      },
    };
    try {
      const { data, error } = await DB.from('Scene_Data')
        .update({
          id: currentProduct?.id,
          title: currentProduct?.title,
          uuid: currentProduct?.uuid,
          default: newUpdatedData,
        })
        .eq('id', currentProduct?.id)
        .select();
      if (error === null) {
        SuccessToastMessage('Finish Updated Successfully!!!');
      } else {
        console.log('Error occurred:', error);
      }
    } catch (err) {
      console.error('Unexpected error occurred:', err);
    }
  }

  async function updateVariant(item) {
    try {
      const { data, error } = await DB.from('Scene_Data')
        .update({
          id: item.id,
          title: item.title,
          uuid: item.uuid,
          defaultVariant: item.defaultVariant,
        })
        .eq('id', item.id)
        .select();

      if (error) {
        throw new Error(error.message);
      }

      console.log('Updated item:', data);
    } catch (err) {
      console.error('Error updating item:', err);
    }
  }

  function handleVariantDefault() {
    productVariants.forEach((item) => {
      if (item.variant === variantName) {
        item.defaultVariant = true;
      } else {
        item.defaultVariant = false;
      }
      updateVariant(item);
    });
    SuccessToastMessage(`${variantName} is set as default Successfully!!!`);
  }
  // console.log(currentMaterialFinish,"currentMaterialFinish")

  useEffect(() => {
    return () => {
      setApiLoading(false);
      setUrl(null);
      setLoading(false);
      setIsARReady(false);
      setIsAR(false);
      setARModelSrc(null);
      setARUnicode(null);
      isOpen = false;
    };
  }, []);
  useEffect(() => {
    if (!ARModelSrc) return;
    setUrl(ARModelSrc);
  }, [ARModelSrc]);

  return (
    <>
      <Viewer3d
        currentFinishes={currentFinishes}
        currentProduct={currentProduct}
        setCurrentProduct={setCurrentProduct}
      />
      {isConfig && (
        // <div className={styles.accordionWrapper}>
        <div className={styles.accordionBody}>
          <strong>{currentProduct.title}</strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 10 }}>
            {productVariants.length > 1 && (
              <div className={styles.accordionItem}>
                <fieldset>
                  <legend>
                    Variant{' '}
                    <span
                      className={styles.saveButton}
                      onClick={handleVariantDefault}
                    >
                      Set as default
                    </span>
                  </legend>
                  <select
                    value={variantName}
                    onChange={(e) => setVariantName(e.target.value)}
                  >
                    <option value="" disabled>
                      Select Variant
                    </option>
                    {productVariants.map((item) => (
                      <option key={item.id} value={item.variant}>
                        {item.variant}
                      </option>
                    ))}
                  </select>
                </fieldset>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {finishOptions.map(
              ({ key, label, data }) =>
                defaultFinish?.[key] && renderSelect(label, data, key),
            )}
          </div>
          {isDebug && (
            <>
              {/* {finishOptions.map(
                  ({ key, label }) =>
                    defaultFinish?.[key] && materialDetails(label, key)
                )} */}
              {materialFinishes &&
                materialFinishes.map((item, key) => (
                  <div key={key}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 8,
                      }}
                    >
                      <span
                        className={styles.accordianIcon}
                        style={{
                          transform: openSections[key]
                            ? 'rotate(90deg)'
                            : 'none',
                        }}
                      ></span>
                      <span
                        className={styles.accordianTitle}
                        onClick={() => toggleAccordion(key)}
                      >
                        {item.finishType}
                        {/* : ({item.default}) */}
                      </span>
                      <span
                        className={styles.saveButton}
                        onClick={() => handleUpdateFinish(item.finishType)}
                      >
                        Save
                      </span>
                    </div>
                    {openSections[key] && (
                      <div style={{ margin: 5 }}>
                        <div style={{ padding: 2 }}>
                          <label
                            className={styles.accordianLabel}
                            htmlFor={`repeatFactorX-${key}`}
                          >
                            Repeat Factor X:
                          </label>
                          <input
                            type="number"
                            id={`repeatFactorX-${key}`}
                            value={item?.reapeatFactor[0]}
                            onChange={handleInputChange(key, 'repeatFactor', 0)}
                            step={MATERIAL_PROPERTIES_STEP}
                          />
                        </div>
                        <div style={{ padding: 2 }}>
                          <label
                            className={styles.accordianLabel}
                            htmlFor={`repeatFactorY-${key}`}
                          >
                            Repeat Factor Y:
                          </label>
                          <input
                            type="number"
                            id={`repeatFactorY-${key}`}
                            value={item?.reapeatFactor[1]}
                            onChange={handleInputChange(key, 'repeatFactor', 1)}
                            step={MATERIAL_PROPERTIES_STEP}
                          />
                        </div>
                        <div style={{ padding: 2 }}>
                          <label
                            className={styles.accordianLabel}
                            htmlFor={`offsetFactorX-${key}`}
                          >
                            Offset Factor X:
                          </label>
                          <input
                            type="number"
                            id={`offsetFactorX-${key}`}
                            value={item?.offsetFactor[0]}
                            onChange={handleInputChange(key, 'offsetFactor', 0)}
                            step={MATERIAL_PROPERTIES_STEP}
                          />
                        </div>
                        <div style={{ padding: 2 }}>
                          <label
                            className={styles.accordianLabel}
                            htmlFor={`offsetFactorY-${key}`}
                          >
                            Offset Factor Y:
                          </label>
                          <input
                            type="number"
                            id={`offsetFactorY-${key}`}
                            value={item?.offsetFactor[1]}
                            onChange={handleInputChange(key, 'offsetFactor', 1)}
                            step={MATERIAL_PROPERTIES_STEP}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              <CameraSection
                currentProduct={currentProduct}
                setCurrentProduct={setCurrentProduct}
              />
            </>
          )}
          <ShowCurrentCameraAngleValueUI />
          <ObjectPosition />
        </div>
        // </div>
      )}

      <div className={styles.actionIcons}>
        {/* <div className={styles.actionIcon} onClick={handleDownloadPdfFile}>
          <img src="/icons/btn_pdf.svg" alt="save pdf" />
        </div> */}

        <button
          title="AR"
          className={`${styles.actionIcon} ${styles.arIcon} ${
            isShare ? styles.active : ''
          }`}
          // onClick={() => setIsShare(!isShare)}
          onClick={() => {
            // for Ar viewer
            if (ARModelSrc && isMobile) setUrl(ARModelSrc);
            else if (ARUnicode && !isMobile) setIsARReady(true);
            else {
              setApiLoading(true);
              setIsAR(true);
            }
            // console.log("clicked");
          }}
        >
          <ARIcon style={{ width: 32, height: 32 }} /> <span>View in AR</span>
        </button>
        <button
          title="Measure"
          className={`${styles.actionIcon} ${
            displayMeasure ? styles.active : ''
          }`}
          onClick={handleMeasureTool}
        >
          <MeasureIcon />
        </button>
        <button
          title="Screenshot"
          className={styles.actionIcon}
          onClick={handleTakeScreenshot}
        >
          <ScreenShotIcon />
        </button>
        <button
          title="Auto-rotate"
          className={`${styles.actionIcon} ${autoRotate ? styles.active : ''}`}
          onClick={() => setAutoRotate(!autoRotate)}
        >
          <RotateIcon />
        </button>
        {isConfiguratorMode || isDebugMode ? (
          <button
            title="Embed your code"
            className={`${styles.actionIcon} ${isEmbed ? styles.active : ''}`}
            onClick={() => setIsEmbed(!isEmbed)}
          >
            <EmbedIcon />
          </button>
        ) : null}
        {isMobile && url ? (
          <ModalViewerUI
            url={url}
            setUrl={setUrl}
            ARUnicode={ARUnicode}
            setLoading={setApiLoading}
          />
        ) : null}
      </div>
    </>
  );
}

const ObjectPosition = ({
  show = false,
  position = {},
  debugObjectPosition = [],
}) => {
  if (!show || !position) return null;
  const { x, y, z } = position;
  const { 0: _x, 1: _y, 2: _z } = debugObjectPosition;
  const posX = parseFloatValue(_x || x);
  const posY = parseFloatValue(_y || y);
  const posZ = parseFloatValue(_z || z);

  const labelStyle = {
    display: 'flex',
    gap: '0.25rem',
    alignItems: 'center',
  };
  const spanStyle = {
    width: '50px',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5rem',
        width: '100%',
      }}
    >
      <label style={labelStyle}>
        <span style={spanStyle}>Pos X</span>
        <input
          type="number"
          value={posX}
          readOnly
          style={{ width: '75px', padding: '4px' }}
        />
      </label>
      <label style={labelStyle}>
        <span style={spanStyle}>Pos Y</span>
        <input
          type="number"
          value={posY}
          readOnly
          style={{ width: '75px', padding: '4px' }}
        />
      </label>
      <label style={labelStyle}>
        <span style={spanStyle}>Pos Z</span>
        <input
          type="number"
          value={posZ}
          readOnly
          style={{ width: '75px', padding: '4px' }}
        />
      </label>
    </div>
  );
};

const ShowCurrentCameraAngleValueUI = ({
  show = false,
  currentCameraAngleValue = {},
}) => {
  if (!show || !currentCameraAngleValue) return null;
  const { x, y, z } = currentCameraAngleValue;
  const camX = parseFloatValue(x);
  const camY = parseFloatValue(y);
  const camZ = parseFloatValue(z);

  const labelStyle = {
    display: 'flex',
    gap: '0.25rem',
    alignItems: 'center',
  };
  const spanStyle = {
    width: '50px',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5rem',
        width: '100%',
      }}
    >
      <label style={labelStyle}>
        <span style={spanStyle}>Cam X</span>
        <input
          type="number"
          value={camX}
          readOnly
          style={{ width: '75px', padding: '4px' }}
        />
      </label>
      <label style={labelStyle}>
        <span style={spanStyle}>Cam Y</span>
        <input
          type="number"
          value={camY}
          readOnly
          style={{ width: '75px', padding: '4px' }}
        />
      </label>
      <label style={labelStyle}>
        <span style={spanStyle}>Cam Z</span>
        <input
          type="number"
          value={camZ}
          readOnly
          style={{ width: '75px', padding: '4px' }}
        />
      </label>
    </div>
  );
};

const ModalViewerUI = ({
  url,
  setUrl,
  setLoading,
  setRef = null,
  ARUnicode,
}) => {
  // if (isAndroid() && ARUnicode && !isOpen) {
  //   isOpen = true;
  //   window.open(`${window.location.origin}/ar/preview/${ARUnicode}`, "_blank");
  //   return;
  // }
  return (
    <ModalViewer
      url={url}
      setUrl={setUrl}
      setLoading={setLoading}
      setRef={setRef}
    />
  );
};

export const ModalViewer = ({ url, setUrl, setLoading, setRef = null }) => {
  const btnRef = useRef();
  const modRef = useRef();
  const { setIsAR, setIsARReady } = useContext(CurrentContext);
  const [modelLoading, setModelLoading] = useState(true);
  const handleLoadModel = () => {
    setModelLoading(false);
    console.log('Model has finished loading.', url);
    if (url && btnRef.current) {
      btnRef?.current?.click?.();
      // console.log("Button Clicked 1");
      console.log('Button Clicked');
      setTimeout(() => {
        setLoading(false);
        setIsAR(false);
        // setARUnicode(null)
        // setARModelSrc(null)
        if (!isAndroid()) {
          setUrl(null);
          setIsARReady(false);
        }
      }, 5000);
    }
  };
  useEffect(() => {
    if (url && modRef?.current) {
      console.log('dEBUG___url', url);
      modRef?.current?.addEventListener('load', handleLoadModel);
    }
    return () => {
      modRef?.current?.removeEventListener('load', handleLoadModel);
    };
  }, [modRef?.current, url]);
  useEffect(() => {
    if (modRef?.current && setRef) setRef(modRef?.current);
  }, [modRef?.current, setRef]);
  if (!url) return;
  console.log('DEBUG___modalViewer');
  return (
    <>
      <Loader loading={modelLoading} transparentBg={true} />
      <model-viewer
        id="model-viewer"
        src={`${url}#canonicalWebPageURL=https://www.libertysafe.com`}
        alt="libertysafe"
        ar-modes="webxr quick-look scene-viewer"
        orientation={'0 0 0 1'}
        camera-controls
        ar
        ar-scale="auto"
        // poster={getCurrentProductThumbnail()}
        shadow-intensity="1"
        ref={modRef}
        // loading="eager"
        // loading="lazy"
        style={{
          width: 0,
          height: 0,
        }}
      >
        <button
          slot="ar-button"
          id="ar-button"
          ref={btnRef}
          style={{ width: '0', height: '0' }}
        ></button>
      </model-viewer>
    </>
  );
};
